/**
 * PendingOfferReviewSheet (Phase A)
 *
 * Read-only review overlay for a single pending marketplace offer. Opens from
 * Home agenda, Opportunities pending-offer card, or any bridge entry point.
 *
 * Hard rules:
 *   • Read-only job preview. No job edits. No new writers.
 *   • Pricing surface: `payoutAmountSnapshot` only — NEVER contract value,
 *     customer revenue, or margin.
 *   • Accept / Decline route through existing `useMarketplaceOffers`.
 *   • `disableActions` (observer mode) disables Accept/Decline buttons.
 *
 * Phase A out-of-scope (deferred):
 *   • Photo / attachment gallery
 *   • Permit readiness chip
 *   • Schedule conflict detection
 *   • Travel distance / map thumbnail
 *   • Arrival window schema additions
 */

import { useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar,
  Clock,
  DollarSign,
  MapPin,
  Package,
  Briefcase,
} from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { useMarketplaceOffers } from "@/hooks/useMarketplaceOffers";
import {
  OFFER_DECLINE_REASONS,
  type OfferDeclineReason,
} from "@/data/contractor/marketplaceOffer";
import { getJobBom, getJobBomSourceLabel } from "@/data/jobBomSelector";
import { formatAddress } from "@/lib/addressUtils";
import { formatStandardDate } from "@/lib/dateFormat";

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return "Expired";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m left`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m left`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h left`;
}

interface Props {
  offerId: string | null;
  onOpenChange: (open: boolean) => void;
  disableActions?: boolean;
}

export function PendingOfferReviewSheet({
  offerId,
  onOpenChange,
  disableActions = false,
}: Props) {
  const { marketplaceOffers, jobs } = useAppData();
  const { id: contractorId } = useCurrentContractor();
  const { accept, decline } = useMarketplaceOffers();

  const offer = useMemo(
    () => marketplaceOffers.find((o) => o.id === offerId) ?? null,
    [marketplaceOffers, offerId],
  );
  const job = useMemo(
    () => (offer ? jobs.find((j) => j.id === offer.jobId) ?? null : null),
    [offer, jobs],
  );

  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState<OfferDeclineReason | undefined>(
    undefined,
  );

  const isOpen = offer != null;
  const bom = useMemo(() => (job ? getJobBom(job) : null), [job]);
  const expiresInMs = offer
    ? new Date(offer.expiresAt).getTime() - Date.now()
    : 0;

  const handleAccept = () => {
    if (!offer) return;
    accept(offer.id, contractorId);
    onOpenChange(false);
  };
  const handleDecline = () => {
    if (!offer || !reason) return;
    decline(offer.id, contractorId, reason);
    setDeclineOpen(false);
    setReason(undefined);
    onOpenChange(false);
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(o) => {
        if (!o) {
          setDeclineOpen(false);
          setReason(undefined);
        }
        onOpenChange(o);
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto p-0"
        data-testid="pending-offer-review-sheet"
      >
        {offer && job && (
          <fieldset
            disabled={disableActions}
            data-testid="pending-offer-review-lockdown"
            data-observer-mode={disableActions ? "true" : "false"}
            className="contents"
          >
            <SheetHeader className="px-5 pt-5 pb-3 border-b">
              <SheetTitle className="text-base font-semibold">
                {job.customerName}
              </SheetTitle>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <Badge variant="outline" className="text-[10px]">
                  {job.projectType ?? "—"}
                </Badge>
                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                  <DollarSign className="h-3 w-3" />
                  {(offer.payoutAmountSnapshot ?? 0).toLocaleString()}
                  <span className="text-[10px] text-muted-foreground font-normal">
                    payout
                  </span>
                </span>
                <span
                  data-testid="offer-time-left"
                  className="inline-flex items-center gap-1 text-warning font-medium"
                >
                  <Clock className="h-3 w-3" />
                  {formatTimeLeft(expiresInMs)}
                </span>
              </div>
              {!declineOpen ? (
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={handleAccept}
                    data-testid="review-sheet-accept"
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setDeclineOpen(true)}
                    data-testid="review-sheet-decline-open"
                  >
                    Decline
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 pt-2">
                  <Select
                    value={reason}
                    onValueChange={(v) => setReason(v as OfferDeclineReason)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Reason for decline" />
                    </SelectTrigger>
                    <SelectContent>
                      {OFFER_DECLINE_REASONS.map((r) => (
                        <SelectItem
                          key={r.value}
                          value={r.value}
                          className="text-xs"
                        >
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      disabled={!reason}
                      onClick={handleDecline}
                      data-testid="review-sheet-decline-confirm"
                    >
                      Confirm decline
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setDeclineOpen(false);
                        setReason(undefined);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </SheetHeader>

            <div className="px-5 py-4 space-y-5 text-xs">
              {/* Schedule */}
              <section data-testid="review-section-schedule">
                <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5 mb-2">
                  <Calendar className="h-3 w-3" /> Schedule
                </h3>
                {job.scheduledInstallDate ? (
                  <p className="font-medium">
                    {formatStandardDate(job.scheduledInstallDate)}
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    Install date not yet scheduled.
                  </p>
                )}
                <p
                  className="text-muted-foreground mt-1"
                  data-testid="review-arrival-window"
                >
                  Arrival window TBD
                </p>
              </section>

              {/* Site */}
              <section data-testid="review-section-site">
                <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5 mb-2">
                  <MapPin className="h-3 w-3" /> Site
                </h3>
                <p className="font-medium">
                  {job.address ? formatAddress(job.address) : "—"}
                </p>
                {job.siteReadiness?.notes && (
                  <p className="text-muted-foreground mt-1">
                    {job.siteReadiness.notes}
                  </p>
                )}
              </section>

              {/* Scope */}
              <section data-testid="review-section-scope">
                <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5 mb-2">
                  <Briefcase className="h-3 w-3" /> Scope
                </h3>
                <p className="font-medium">{job.projectType ?? "—"}</p>
                {job.projectConfig?.autoProjectType && (
                  <p className="text-muted-foreground mt-0.5">
                    {job.projectConfig.autoProjectType}
                  </p>
                )}

                <div className="mt-3" data-testid="review-section-bom">
                  <div className="flex items-center justify-between mb-1.5">
                    <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5">
                      <Package className="h-3 w-3" /> Bill of Materials
                    </h4>
                    {bom && (
                      <Badge variant="outline" className="text-[9px]">
                        {getJobBomSourceLabel(bom.source)}
                      </Badge>
                    )}
                  </div>
                  {bom && bom.items.length > 0 ? (
                    <ul className="space-y-1 rounded-md border border-border/60 bg-muted/10 p-2">
                      {bom.items.map((it, idx) => (
                        <li
                          key={`${it.sku}-${idx}`}
                          className="flex items-center justify-between gap-2"
                          data-testid="review-bom-item"
                        >
                          <span className="truncate">{it.name}</span>
                          <span className="text-muted-foreground shrink-0">
                            × {it.quantity}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground">
                      No BOM bound — confirm spec with dispatcher.
                    </p>
                  )}
                </div>
              </section>
            </div>
          </fieldset>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default PendingOfferReviewSheet;
